import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { useGetSpinActivity } from '../../queries/marketing';

function PrizeCell({ row }) {
  return <span>{row.prize_icon} {row.prize_name}</span>;
}

function StatusTag({ row, t }) {
  if (row.redeemed) return <Tag severity="success" value={t('marketing.act_spin_status_redeemed')} />;
  if (row.expired)  return <Tag severity="danger"  value={t('marketing.act_spin_status_expired')} />;
  return <Tag severity="warning" value={t('marketing.act_spin_status_active')} />;
}

function ExpiresCell({ row, t }) {
  if (!row.expires_at) return <span className="text-400">—</span>;
  const d = new Date(row.expires_at);
  if (row.redeemed) return <span className="text-400">{d.toLocaleDateString()}</span>;
  if (row.expired)  return <span className="text-400">{d.toLocaleDateString()}</span>;
  const diffH = Math.floor((d - Date.now()) / 3_600_000);
  if (diffH < 24) {
    return <span className="text-orange-500 font-semibold">{diffH}h {t('marketing.act_spin_left')}</span>;
  }
  return <span>{d.toLocaleDateString()}</span>;
}

export default function SpinActivityPanel({ period, from, to }) {
  const { t } = useTranslation();

  const { data, isLoading } = useGetSpinActivity(
    from && to ? { period, from, to } : { period }
  );

  const summary = data?.summary ?? {};
  const winners = data?.recent_winners ?? [];
  const byPrize = data?.by_prize ?? [];

  const kpiCards = [
    { key: 'total_plays',     color: 'var(--blue-500)' },
    { key: 'total_wins',      color: 'var(--purple-500)' },
    { key: 'total_redeemed',  color: 'var(--green-500)' },
    { key: 'redemption_rate', color: 'var(--teal-500)', suffix: '%' },
    { key: 'new_crm_records', color: 'var(--orange-500)' },
  ];

  return (
    <div className="mb-5">
      <h3 className="mt-0 mb-3">{t('marketing.act_spin_title')}</h3>

      {/* KPI cards */}
      <div className="grid mb-4">
        {kpiCards.map((c) => (
          <div key={c.key} className="col-6 md:col-4 lg:col-2">
            <div className="surface-card border-round p-3 text-center">
              <div className="text-2xl font-bold" style={{ color: c.color }}>
                {summary[c.key] != null
                  ? `${summary[c.key]}${c.suffix ?? ''}`
                  : '—'}
              </div>
              <div className="text-500 text-sm">
                {t(`marketing.act_spin_${c.key}`)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid">
        {/* Recent winners table */}
        <div className="col-12 lg:col-8">
          <div className="font-semibold mb-2">{t('marketing.act_spin_winners_title')}</div>
          <DataTable
            value={winners}
            loading={isLoading}
            dataKey="prize_code"
            emptyMessage={t('marketing.act_spin_winners_empty')}
            responsiveLayout="scroll"
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
          >
            <Column
              header={t('marketing.act_spin_col_player')}
              body={(r) => r.player_name || <span className="text-400">{t('marketing.act_spin_anon')}</span>}
            />
            <Column
              header={t('marketing.act_spin_col_prize')}
              body={(r) => <PrizeCell row={r} />}
            />
            <Column
              header={t('marketing.act_spin_col_code')}
              body={(r) => <code className="text-sm">{r.prize_code}</code>}
            />
            <Column
              header={t('marketing.act_spin_col_status')}
              body={(r) => <StatusTag row={r} t={t} />}
            />
            <Column
              header={t('marketing.act_spin_col_expires')}
              body={(r) => <ExpiresCell row={r} t={t} />}
            />
          </DataTable>
        </div>

        {/* By-prize breakdown */}
        <div className="col-12 lg:col-4">
          <div className="font-semibold mb-2">{t('marketing.act_spin_by_prize_title')}</div>
          <DataTable
            value={byPrize}
            dataKey="prize_name"
            emptyMessage="—"
            size="small"
            responsiveLayout="scroll"
          >
            <Column
              header={t('marketing.act_spin_col_prize')}
              body={(r) => <PrizeCell row={r} />}
            />
            <Column field="wins"     header={t('marketing.act_spin_col_wins')} />
            <Column field="redeemed" header={t('marketing.act_spin_col_redeemed')} />
            <Column
              header={t('marketing.act_spin_col_rate')}
              body={(r) => (r.rate != null ? `${r.rate}%` : '—')}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
}
